import { lazy, ComponentType, LazyExoticComponent } from 'react';

const HARD_RELOAD_KEY = '__lazy_chunk_hard_reload__';

/**
 * Wraps React.lazy with automatic retry on stale-chunk / dynamic-import errors.
 * After `retries` failures, triggers ONE hard reload (bypassing HTTP cache) so
 * the browser re-fetches a fresh index.html with the current asset manifest.
 * If the hard reload already happened in this session, the error propagates to
 * the error boundary so the user sees a real fallback.
 */
export function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): LazyExoticComponent<T> {
  return lazy(() => attempt(factory, retries));
}

function isChunkLoadError(err: any): boolean {
  const msg = (err?.message ?? '').toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('chunkloaderror') ||
    msg.includes('importing a module script failed') ||
    msg.includes('module script') ||
    msg.includes('error loading')
  );
}

function alreadyHardReloaded(): boolean {
  try {
    return sessionStorage.getItem(HARD_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

function markHardReloaded() {
  try {
    sessionStorage.setItem(HARD_RELOAD_KEY, '1');
  } catch {
    /* ignore */
  }
}

async function attempt<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries: number,
): Promise<{ default: T }> {
  try {
    const mod = await factory();
    // Successful load — clear the hard-reload guard so future stale chunks can recover again.
    try { sessionStorage.removeItem(HARD_RELOAD_KEY); } catch { /* ignore */ }
    return mod;
  } catch (err: any) {
    if (!isChunkLoadError(err)) throw err;

    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 600));
      return attempt(factory, retries - 1);
    }

    // Final fallback: force one hard reload to refresh index.html + manifest.
    if (typeof window !== 'undefined' && !alreadyHardReloaded()) {
      markHardReloaded();
      // Cache-bust the URL so intermediaries re-validate.
      const url = new URL(window.location.href);
      url.searchParams.set('_r', Date.now().toString(36));
      window.location.replace(url.toString());
      // Return a never-resolving promise so React doesn't render the error
      // before the navigation happens.
      return new Promise<never>(() => {});
    }

    throw err;
  }
}
